<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.anos"
                            :validations="validations.ano"
                            @change="loadFrom('ano')"
                            label="Ano"
                            show-default-option
                            v-model="model.ano"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-select
                            :disabled="!model.ano"
                            :options="form.meses"
                            :validations="validations.mes"
                            @change="loadFrom('mes')"
                            label="Mês"
                            show-default-option
                            v-model="model.mes"
                        />
                    </rkm-col>

                    <rkm-col cols="7">
                        <rkm-form-select
                            :disabled="!model.mes"
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="5">
                        <rkm-form-select
                            :disabled="!model.unidadeId"
                            :options="form.infraestruturas"
                            @change="loadFrom('infraestrutura')"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :disabled="!model.infraestruturaId"
                            :options="form.equipes"
                            default-option-text="Todas"
                            label="Equipe"
                            show-default-option
                            text-field="descricao"
                            v-model="model.equipeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-button
                    :disabled="!resultData"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="!resultData"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <template v-if="resultData">
                <rkm-table
                    v-for="(data, key) in resultData"
                    :class="{'table-resultado': true, 'mt-4': key !== 'levantamentoQuantitativo'}"
                    :fields="data.itens.find(item => 'percentual' in item) ? columns : columnsWithoutPercentual"
                    :items="data.itens"
                    :key="key"
                    hide-filters
                    hide-pagination
                >
                    <template #thead-top>
                        <rkm-tr>
                            <rkm-th
                                :colspan="data.itens.find(item => 'percentual' in item) ? 3 : 2"
                                class="text-center"
                            >
                                {{ data.titulo }}
                            </rkm-th>
                        </rkm-tr>
                    </template>

                    <template
                        v-if="data.rodape"
                        #afterTable
                    >
                        <div class="table-footer">
                            {{ data.rodape }}
                        </div>
                    </template>
                </rkm-table>
            </template>
        </rkm-area>
    </div>
</template>

<script>
// Vuex.
import { mapActions } from 'vuex'

// Mixins.
import { repository, error, object, stringCase, file } from '@/mixins'

export default {
    computed: {
        validations() {

            return {
                ano: { required: true },
                mes: { required: true },
            }
        },
        columnsWithoutPercentual() {

            const columns = []

            this.columns.forEach(column => {

                if (column.key !== 'percentual') {

                    columns.push(column)
                }
            })

            return columns
        }
    },
    created() {

        this.repositories = {
            relatorioIndicadorSad: this.getConectaSusRepository('atencaoBasica.relatorioIndicadorSad'),
            impressao: this.getConectaSusRepository('impressao.atencaoBasica'),
            exportacao: this.getConectaSusRepository('exportacao.atencaoBasica'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            resultData: null,
            columns: [
                { key: 'descricao', label: 'Quantitativo' },
                { key: 'quantidade', label: 'Quantidade', class: 'text-center quantidade-percentual' },
                { key: 'percentual', label: '%', class: 'text-center quantidade-percentual' },
            ]
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioIndicadorSad.list()
                .then(({ data }) => {

                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioIndicadorSad.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(currentField) {

            const fields = {
                ano: { form: 'anos', model: 'ano' },
                mes: { form: 'meses', model: 'mes' },
                unidade: { form: 'unidades', model: 'unidadeId' },
                infraestrutura: { form: 'infraestruturas', model: 'infraestruturaId' },
                equipe: { form: 'equipes', model: 'equipeId' },
            }

            let clear = false

            for (const field in fields) {

                if (clear) {

                    if ('form' in fields[field]) {

                        this.$set(this.form, fields[field].form, [])
                    }

                    this.$set(this.model, fields[field].model, null)
                }
                else if (field === currentField) {

                    clear = true
                }
            }
        },
        search() {

            this.setLoading()

            this.repositories.relatorioIndicadorSad.index(this.prepareData())
                .then(({ data }) => this.resultData = data)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioIndicadorSad(this.prepareData())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.relatorioIndicadorSad(this.prepareData())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareData() {

            return this.camelObjectKeysToSnakeRecursive(this.clone(this.model))
        },
    },
    mixins: [repository, error, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.resultData = null
            }
        },
    }
}
</script>

<style scoped>
.table-resultado >>> .table-footer {
    color: var(--gray-dark);
    background: #f1f1f1;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
}

.table-resultado >>> .quantidade-percentual {
    width: 12%;
}
</style>